import { createRootRoute, createRoute, createRouter, lazyRouteComponent, redirect } from "@tanstack/react-router";
import { modelCatalogSearch } from "./lib/model-catalog-search";
import { AdminShell } from "./components/admin/admin-shell";
import { DocsShell } from "./components/docs-shell";

import {
  AppShell,
  DashboardPage,
  LoginPage,
  NotFoundPage,
} from "./public-pages";

// 前台与管理后台是两个布局空间：root 只做分支，404 归前台壳渲染。
const rootRoute = createRootRoute({});

const publicRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "public",
  component: AppShell,
  notFoundComponent: NotFoundPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/",
  component: DashboardPage,
});

const loginRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "login",
  component: LoginPage,
});

const docsRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "docs",
  component: DocsShell,
});

const docsIndexRoute = createRoute({
  getParentRoute: () => docsRoute,
  path: "/",
  beforeLoad: () => { throw redirect({ to: "/docs/errors", replace: true }); },
});

const publicErrorDocsRoute = createRoute({
  getParentRoute: () => docsRoute,
  path: "errors",
  component: lazyRouteComponent(() => import("./public-error-docs-page"), "PublicErrorDocsPage"),
});

const usageQueryDocsRoute = createRoute({
  getParentRoute: () => docsRoute,
  path: "usage",
  component: lazyRouteComponent(() => import("./usage-query-docs-page"), "UsageQueryDocsPage"),
});

const sandboxRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "sandbox",
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.model === "string" ? { model: search.model } : {}),
  }),
  component: lazyRouteComponent(() => import("./user-pages"), "SandboxPage"),
});

const modelsRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "explore",
  validateSearch: modelCatalogSearch,
  component: lazyRouteComponent(() => import("./user-pages"), "ModelsPage"),
});

const modelRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "models/$",
  beforeLoad: ({ params, location }) => {
    if (!params._splat) throw redirect({ to: "/explore", search: modelCatalogSearch(location.search), replace: true });
  },
  validateSearch: (search: Record<string, unknown>) => {
    const tabs = new Set(["playground", "api", "examples", "pricing", "requests", "analytics", "schema"]);
    return {
      ...(typeof search.tab === "string" && tabs.has(search.tab) ? { tab: search.tab } : {}),
      ...(typeof search.taskId === "string" ? { taskId: search.taskId } : {}),
      ...(typeof search.requestId === "string" && search.requestId.trim() ? { requestId: search.requestId.trim() } : {}),
      ...(typeof search.sessionId === "string" && search.sessionId.trim() ? { sessionId: search.sessionId.trim() } : {}),
    };
  },
  component: lazyRouteComponent(() => import("./user-pages"), "ModelPage"),
});

const requestsRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "requests",
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.id === "string" && search.id.trim() ? { id: search.id.trim() } : {}),
  }),
  component: lazyRouteComponent(() => import("./user-pages"), "RequestsPage"),
});

const settingsRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "settings/$",
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.start === "string" ? { start: search.start } : {}),
    ...(typeof search.end === "string" ? { end: search.end } : {}),
    ...(search.view === "summary" ? { view: "summary" } : {}),
  }),
  component: lazyRouteComponent(() => import("./user-pages"), "SettingsPage"),
});

const adminShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "admin-shell",
  component: AdminShell,
});

const adminRoute = createRoute({
  getParentRoute: () => adminShellRoute,
  path: "admin/$",
  // 透传所有 query 参数：用量统计等页面把筛选条件保存在 URL 里
  validateSearch: (search: Record<string, unknown>) => search as Record<string, string>,
  component: lazyRouteComponent(() => import("./admin-pages"), "AdminPage"),
});

const routeTree = rootRoute.addChildren([
  publicRoute.addChildren([
    dashboardRoute,
    loginRoute,
    docsRoute.addChildren([docsIndexRoute, publicErrorDocsRoute, usageQueryDocsRoute]),
    sandboxRoute,
    modelsRoute,
    modelRoute,
    requestsRoute,
    settingsRoute,
  ]),
  adminShellRoute.addChildren([adminRoute]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 10_000,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
